import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, ArrowUpDownIcon, EyeIcon, TrendingUpIcon, TrendingDownIcon, CalendarIcon, LockIcon, InfoIcon, BellIcon, MinusIcon, ChevronRightIcon, EyeOffIcon, ArchiveIcon, MedalIcon, GripVerticalIcon } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { NudgeModal } from '../components/NudgeModal';
type EducatorStatus = 'Active' | 'Low Activity' | 'Inactive' | 'Archived';
type SortField = 'name' | 'assessments' | 'participants' | 'completion' | 'impact';
type SortDirection = 'asc' | 'desc';
type MetricType = 'educators' | 'assessments' | 'completion' | 'avgScore';
type Role = 'Primary Admin' | 'Secondary Admin' | 'Educator';
interface EducatorMonthlyData {
  id: number;
  name: string;
  email: string;
  status: EducatorStatus;
  assessmentsShared: number;
  studentsEngaged: number;
  completionRate: number;
  avgScore: number;
  engagementScore: number; // Total unique student completions across eligible assessments
  eligibleAssessments: number; // Assessments with ≥2 unique starters
  lastContribution?: string; // Date string like "Apr 15"
  grades?: string[];
  subjects?: string[];
}
interface MonthData {
  month: string;
  fullMonth: string;
  educators: EducatorMonthlyData[];
}
interface AssessmentCategory {
  name: string;
  count: number;
  avgScore: number;
}
interface MonthlyMetrics {
  month: string;
  activeEducators: number;
  assessmentsShared: number;
  completionRate: number;
  avgScore: number;
}
export function OrganizationOverview() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('impact');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('assessments');
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(5);
  const [includeCurrentMonth, setIncludeCurrentMonth] = useState(true);
  const [showArchivedEducators, setShowArchivedEducators] = useState(false);
  // Nudge modal state
  const [selectedEducatorForNudge, setSelectedEducatorForNudge] = useState<EducatorMonthlyData | null>(null);
  const [isNudgeModalOpen, setIsNudgeModalOpen] = useState(false);
  // Column resize state
  const [columnWidths, setColumnWidths] = useState({
    educator: 220,
    assessments: 100,
    participants: 110,
    context: 140,
    completion: 100,
    impact: 90
  });
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const tableRef = useRef<HTMLTableElement>(null);
  // Column resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent, column: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(column);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = columnWidths[column as keyof typeof columnWidths];
  }, [columnWidths]);
  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    const diff = e.clientX - resizeStartX.current;
    const newWidth = Math.max(60, resizeStartWidth.current + diff);
    setColumnWidths(prev => ({
      ...prev,
      [isResizing]: newWidth
    }));
  }, [isResizing]);
  const handleResizeEnd = useCallback(() => {
    setIsResizing(null);
  }, []);
  // Add/remove resize event listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);
  // SIMULATED AUTH CONTEXT
  // In a real app, this would come from a context provider
  const currentUserRole: Role = 'Primary Admin'; // Change to 'Educator' to test restricted view
  const currentUserId = 1; // ID of the current user
  // Demo data - Monthly educator details
  const monthlyEducatorData: MonthData[] = [{
    month: 'Nov',
    fullMonth: 'November 2023',
    educators: [{
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah.j@school.edu',
      status: 'Active',
      assessmentsShared: 4,
      studentsEngaged: 38,
      completionRate: 70,
      avgScore: 65,
      engagementScore: 28,
      eligibleAssessments: 3,
      lastContribution: 'Nov 28',
      grades: ['Grade 9', 'Grade 10'],
      subjects: ['Physics', 'Mathematics']
    }, {
      id: 2,
      name: 'Michael Chen',
      email: 'mchen@school.edu',
      status: 'Active',
      assessmentsShared: 5,
      studentsEngaged: 35,
      completionRate: 68,
      avgScore: 63,
      engagementScore: 24,
      eligibleAssessments: 4,
      lastContribution: 'Nov 30',
      grades: ['Grade 11', 'Grade 12'],
      subjects: ['Chemistry', 'Biology']
    }, {
      id: 3,
      name: 'Emily Davis',
      email: 'emily.d@school.edu',
      status: 'Active',
      assessmentsShared: 3,
      studentsEngaged: 20,
      completionRate: 65,
      avgScore: 62,
      engagementScore: 13,
      eligibleAssessments: 2,
      lastContribution: 'Nov 25',
      grades: ['Grade 8', 'Grade 9'],
      subjects: ['English', 'Literature']
    }, {
      id: 5,
      name: 'Jessica Martinez',
      email: 'jessica.m@school.edu',
      status: 'Active',
      assessmentsShared: 6,
      studentsEngaged: 42,
      completionRate: 72,
      avgScore: 67,
      engagementScore: 35,
      eligibleAssessments: 5,
      lastContribution: 'Nov 29',
      grades: ['Grade 10', 'Grade 11', 'Grade 12'],
      subjects: ['Spanish', 'French']
    }]
  }, {
    month: 'Dec',
    fullMonth: 'December 2023',
    educators: [{
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah.j@school.edu',
      status: 'Active',
      assessmentsShared: 5,
      studentsEngaged: 40,
      completionRate: 73,
      avgScore: 67,
      engagementScore: 32,
      eligibleAssessments: 4,
      lastContribution: 'Dec 20',
      grades: ['Grade 9', 'Grade 10'],
      subjects: ['Physics', 'Mathematics']
    }, {
      id: 2,
      name: 'Michael Chen',
      email: 'mchen@school.edu',
      status: 'Active',
      assessmentsShared: 6,
      studentsEngaged: 37,
      completionRate: 71,
      avgScore: 65,
      engagementScore: 27,
      eligibleAssessments: 5,
      lastContribution: 'Dec 22',
      grades: ['Grade 11', 'Grade 12'],
      subjects: ['Chemistry', 'Biology']
    }, {
      id: 3,
      name: 'Emily Davis',
      email: 'emily.d@school.edu',
      status: 'Active',
      assessmentsShared: 4,
      studentsEngaged: 22,
      completionRate: 68,
      avgScore: 64,
      engagementScore: 15,
      eligibleAssessments: 3,
      lastContribution: 'Dec 18',
      grades: ['Grade 8', 'Grade 9'],
      subjects: ['English', 'Literature']
    }, {
      id: 5,
      name: 'Jessica Martinez',
      email: 'jessica.m@school.edu',
      status: 'Active',
      assessmentsShared: 7,
      studentsEngaged: 45,
      completionRate: 75,
      avgScore: 69,
      engagementScore: 38,
      eligibleAssessments: 6,
      lastContribution: 'Dec 21',
      grades: ['Grade 10', 'Grade 11', 'Grade 12'],
      subjects: ['Spanish', 'French']
    }]
  }, {
    month: 'Jan',
    fullMonth: 'January 2024',
    educators: [{
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah.j@school.edu',
      status: 'Active',
      assessmentsShared: 7,
      studentsEngaged: 43,
      completionRate: 76,
      avgScore: 70,
      engagementScore: 36,
      eligibleAssessments: 6,
      lastContribution: 'Jan 30',
      grades: ['Grade 9', 'Grade 10'],
      subjects: ['Physics', 'Mathematics']
    }, {
      id: 2,
      name: 'Michael Chen',
      email: 'mchen@school.edu',
      status: 'Active',
      assessmentsShared: 8,
      studentsEngaged: 39,
      completionRate: 74,
      avgScore: 68,
      engagementScore: 31,
      eligibleAssessments: 7,
      lastContribution: 'Jan 29',
      grades: ['Grade 11', 'Grade 12'],
      subjects: ['Chemistry', 'Biology']
    }, {
      id: 3,
      name: 'Emily Davis',
      email: 'emily.d@school.edu',
      status: 'Active',
      assessmentsShared: 5,
      studentsEngaged: 25,
      completionRate: 70,
      avgScore: 66,
      engagementScore: 18,
      eligibleAssessments: 4,
      lastContribution: 'Jan 28',
      grades: ['Grade 8', 'Grade 9'],
      subjects: ['English', 'Literature']
    }, {
      id: 5,
      name: 'Jessica Martinez',
      email: 'jessica.m@school.edu',
      status: 'Active',
      assessmentsShared: 8,
      studentsEngaged: 48,
      completionRate: 78,
      avgScore: 71,
      engagementScore: 42,
      eligibleAssessments: 7,
      lastContribution: 'Jan 31',
      grades: ['Grade 10', 'Grade 11', 'Grade 12'],
      subjects: ['Spanish', 'French']
    }]
  }, {
    month: 'Feb',
    fullMonth: 'February 2024',
    educators: [{
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah.j@school.edu',
      status: 'Active',
      assessmentsShared: 6,
      studentsEngaged: 42,
      completionRate: 75,
      avgScore: 69,
      engagementScore: 34,
      eligibleAssessments: 5,
      lastContribution: 'Feb 28',
      grades: ['Grade 9', 'Grade 10'],
      subjects: ['Physics', 'Mathematics']
    }, {
      id: 2,
      name: 'Michael Chen',
      email: 'mchen@school.edu',
      status: 'Active',
      assessmentsShared: 7,
      studentsEngaged: 38,
      completionRate: 73,
      avgScore: 67,
      engagementScore: 29,
      eligibleAssessments: 6,
      lastContribution: 'Feb 27',
      grades: ['Grade 11', 'Grade 12'],
      subjects: ['Chemistry', 'Biology']
    }, {
      id: 3,
      name: 'Emily Davis',
      email: 'emily.d@school.edu',
      status: 'Low Activity',
      assessmentsShared: 3,
      studentsEngaged: 18,
      completionRate: 68,
      avgScore: 65,
      engagementScore: 10,
      eligibleAssessments: 2,
      lastContribution: 'Feb 15',
      grades: ['Grade 8', 'Grade 9'],
      subjects: ['English', 'Literature']
    }, {
      id: 5,
      name: 'Jessica Martinez',
      email: 'jessica.m@school.edu',
      status: 'Active',
      assessmentsShared: 9,
      studentsEngaged: 50,
      completionRate: 76,
      avgScore: 72,
      engagementScore: 45,
      eligibleAssessments: 8,
      lastContribution: 'Feb 29',
      grades: ['Grade 10', 'Grade 11', 'Grade 12'],
      subjects: ['Spanish', 'French']
    }]
  }, {
    month: 'Mar',
    fullMonth: 'March 2024',
    educators: [{
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah.j@school.edu',
      status: 'Active',
      assessmentsShared: 8,
      studentsEngaged: 45,
      completionRate: 82,
      avgScore: 75,
      engagementScore: 41,
      eligibleAssessments: 7,
      lastContribution: 'Mar 30',
      grades: ['Grade 9', 'Grade 10'],
      subjects: ['Physics', 'Mathematics']
    }, {
      id: 2,
      name: 'Michael Chen',
      email: 'mchen@school.edu',
      status: 'Active',
      assessmentsShared: 9,
      studentsEngaged: 40,
      completionRate: 78,
      avgScore: 71,
      engagementScore: 35,
      eligibleAssessments: 8,
      lastContribution: 'Mar 29',
      grades: ['Grade 11', 'Grade 12'],
      subjects: ['Chemistry', 'Biology']
    }, {
      id: 3,
      name: 'Emily Davis',
      email: 'emily.d@school.edu',
      status: 'Low Activity',
      assessmentsShared: 2,
      studentsEngaged: 12,
      completionRate: 65,
      avgScore: 68,
      engagementScore: 6,
      eligibleAssessments: 1,
      lastContribution: 'Mar 10',
      grades: ['Grade 8', 'Grade 9'],
      subjects: ['English', 'Literature']
    }, {
      id: 4,
      name: 'Robert Wilson',
      email: 'rwilson@school.edu',
      status: 'Inactive',
      assessmentsShared: 0,
      studentsEngaged: 0,
      completionRate: 0,
      avgScore: 0,
      engagementScore: 0,
      eligibleAssessments: 0,
      lastContribution: 'Feb 20',
      grades: ['Grade 6', 'Grade 7'],
      subjects: ['Science', 'Math']
    }, {
      id: 5,
      name: 'Jessica Martinez',
      email: 'jessica.m@school.edu',
      status: 'Active',
      assessmentsShared: 12,
      studentsEngaged: 52,
      completionRate: 88,
      avgScore: 79,
      engagementScore: 52,
      eligibleAssessments: 11,
      lastContribution: 'Mar 31',
      grades: ['Grade 10', 'Grade 11', 'Grade 12'],
      subjects: ['Spanish', 'French']
    }]
  }, {
    month: 'Apr',
    fullMonth: 'April 2024',
    educators: [{
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah.j@school.edu',
      status: 'Active',
      assessmentsShared: 6,
      studentsEngaged: 43,
      completionRate: 80,
      avgScore: 74,
      engagementScore: 38,
      eligibleAssessments: 5,
      lastContribution: 'Apr 25',
      grades: ['Grade 9', 'Grade 10'],
      subjects: ['Physics', 'Mathematics']
    }, {
      id: 2,
      name: 'Michael Chen',
      email: 'mchen@school.edu',
      status: 'Active',
      assessmentsShared: 7,
      studentsEngaged: 38,
      completionRate: 76,
      avgScore: 70,
      engagementScore: 31,
      eligibleAssessments: 6,
      lastContribution: 'Apr 24',
      grades: ['Grade 11', 'Grade 12'],
      subjects: ['Chemistry', 'Biology']
    }, {
      id: 3,
      name: 'Emily Davis',
      email: 'emily.d@school.edu',
      status: 'Low Activity',
      assessmentsShared: 1,
      studentsEngaged: 8,
      completionRate: 62,
      avgScore: 66,
      engagementScore: 0,
      eligibleAssessments: 0,
      lastContribution: 'Apr 5',
      grades: ['Grade 8', 'Grade 9'],
      subjects: ['English', 'Literature']
    }, {
      id: 4,
      name: 'Robert Wilson',
      email: 'rwilson@school.edu',
      status: 'Inactive',
      assessmentsShared: 0,
      studentsEngaged: 0,
      completionRate: 0,
      avgScore: 0,
      engagementScore: 0,
      eligibleAssessments: 0,
      lastContribution: 'Mar 10',
      grades: ['Grade 6', 'Grade 7'],
      subjects: ['Science', 'Math']
    }, {
      id: 5,
      name: 'Jessica Martinez',
      email: 'jessica.m@school.edu',
      status: 'Active',
      assessmentsShared: 10,
      studentsEngaged: 50,
      completionRate: 86,
      avgScore: 77,
      engagementScore: 48,
      eligibleAssessments: 9,
      lastContribution: 'Apr 26',
      grades: ['Grade 10', 'Grade 11', 'Grade 12'],
      subjects: ['Spanish', 'French']
    }]
  }];
  // Calculate monthly metrics from educator data
  const monthlyData: MonthlyMetrics[] = monthlyEducatorData.map(monthData => {
    const activeEducators = monthData.educators.filter(e => e.assessmentsShared > 0).length;
    const totalAssessments = monthData.educators.reduce((sum, e) => sum + e.assessmentsShared, 0);
    const avgCompletion = Math.round(monthData.educators.filter(e => e.assessmentsShared > 0).reduce((sum, e) => sum + e.completionRate, 0) / activeEducators);
    const avgScore = Math.round(monthData.educators.filter(e => e.assessmentsShared > 0).reduce((sum, e) => sum + e.avgScore, 0) / activeEducators);
    return {
      month: monthData.month,
      activeEducators,
      assessmentsShared: totalAssessments,
      completionRate: avgCompletion,
      avgScore
    };
  });
  // Filter data based on includeCurrentMonth toggle
  const displayMonthlyData = includeCurrentMonth ? monthlyData : monthlyData.slice(0, -1); // Exclude last month (April)
  // Current month metrics - use last available month based on toggle
  const currentMetrics = displayMonthlyData[displayMonthlyData.length - 1];
  const previousMetrics = displayMonthlyData[displayMonthlyData.length - 2];
  // Calculate trends
  const educatorsTrend = currentMetrics.activeEducators - previousMetrics.activeEducators;
  const assessmentsTrend = currentMetrics.assessmentsShared - previousMetrics.assessmentsShared;
  const completionTrend = currentMetrics.completionRate - previousMetrics.completionRate;
  const scoreTrend = currentMetrics.avgScore - previousMetrics.avgScore;
  // Adjust selected month index when toggle changes
  useEffect(() => {
    if (!includeCurrentMonth && selectedMonthIndex === monthlyData.length - 1) {
      // If current month was selected and we toggle it off, select previous month
      setSelectedMonthIndex(monthlyData.length - 2);
    }
  }, [includeCurrentMonth, selectedMonthIndex, monthlyData.length]);
  // Calculate effective selected index BEFORE using it
  const effectiveSelectedIndex = includeCurrentMonth ? selectedMonthIndex : Math.min(selectedMonthIndex, monthlyData.length - 2);
  // Get selected month data - filter archived educators if needed
  const selectedMonthData = {
    ...monthlyEducatorData[effectiveSelectedIndex],
    educators: showArchivedEducators ? monthlyEducatorData[effectiveSelectedIndex].educators : monthlyEducatorData[effectiveSelectedIndex].educators.filter(e => e.status !== 'Archived')
  };
  const selectedMonthMetrics = monthlyData[effectiveSelectedIndex];
  // Determine if selected month is the current month (for nudge logic)
  const isCurrentMonth = includeCurrentMonth && effectiveSelectedIndex === monthlyData.length - 1;
  // Get previous month data for trend calculation
  const previousMonthData = effectiveSelectedIndex > 0 ? monthlyEducatorData[effectiveSelectedIndex - 1] : null;
  // Helper function to get educator trend
  const getEducatorTrend = (currentEducator: EducatorMonthlyData) => {
    if (!previousMonthData) return null;
    const prevEducator = previousMonthData.educators.find(e => e.id === currentEducator.id);
    if (!prevEducator) return null;
    const assessmentsDiff = currentEducator.assessmentsShared - prevEducator.assessmentsShared;
    if (assessmentsDiff > 0) return 'up';
    if (assessmentsDiff < 0) return 'down';
    return 'same';
  };
  // Helper function to get completion rate color
  const getCompletionColor = (rate: number) => {
    if (rate >= 80) return 'text-[#00B75F] font-semibold';
    if (rate >= 50) return 'text-text-primary font-medium';
    return 'text-[#FF9D33] font-medium';
  };
  // Helper function to check if educator needs nudge (activity window approach)
  const needsNudge = (educator: EducatorMonthlyData) => {
    const ACTIVITY_WINDOW_DAYS = 14; // This would come from settings in production
    // No activity recorded at all
    if (!educator.lastContribution) {
      return true;
    }
    // Calculate days since last activity
    const lastActivityDate = parseLastContribution(educator.lastContribution);
    if (!lastActivityDate) {
      return true;
    }
    // Simulate "today" as April 28, 2024 for demo purposes
    // In production, this would be: const now = new Date()
    const now = new Date(2024, 3, 28); // April 28, 2024
    const daysSince = Math.floor((now.getTime() - lastActivityDate.getTime()) / (1000 * 60 * 60 * 24));
    // Flag if exceeds activity window
    return daysSince > ACTIVITY_WINDOW_DAYS;
  };
  // Helper to parse "Mar 15" format to Date
  const parseLastContribution = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    // Parse "Mar 15" or "Apr 25" format
    const months: {
      [key: string]: number;
    } = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11
    };
    const parts = dateStr.split(' ');
    if (parts.length !== 2) return null;
    const month = months[parts[0]];
    const day = parseInt(parts[1]);
    if (month === undefined || isNaN(day)) return null;
    const year = 2024; // Use 2024 for consistency with mock data
    return new Date(year, month, day);
  };
  // Helper to get days since last activity
  const getDaysSinceLastActivity = (educator: EducatorMonthlyData): number | null => {
    if (!educator.lastContribution) return null;
    const lastActivityDate = parseLastContribution(educator.lastContribution);
    if (!lastActivityDate) return null;
    // Simulate "today" as April 28, 2024 for demo purposes
    // In production, this would be: const now = new Date()
    const now = new Date(2024, 3, 28); // April 28, 2024
    return Math.floor((now.getTime() - lastActivityDate.getTime()) / (1000 * 60 * 60 * 24));
  };
  // Helper to format days since for display
  const formatDaysSince = (days: number | null): string => {
    if (days === null) return 'Never';
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    if (days < 7) return `${days} days ago`;
    if (days < 14) return '1 week ago';
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`;
  };
  const isLowActivity = (educator: EducatorMonthlyData) => {
    return educator.assessmentsShared > 0 && educator.assessmentsShared <= 2;
  };
  const isZeroActivity = (educator: EducatorMonthlyData) => {
    return educator.assessmentsShared === 0;
  };
  const getMetricData = (metric: MetricType) => {
    switch (metric) {
      case 'educators':
        return displayMonthlyData.map(m => m.activeEducators);
      case 'assessments':
        return displayMonthlyData.map(m => m.assessmentsShared);
      case 'completion':
        return displayMonthlyData.map(m => m.completionRate);
      case 'avgScore':
        return displayMonthlyData.map(m => m.avgScore);
    }
  };
  const getMetricLabel = (metric: MetricType) => {
    switch (metric) {
      case 'educators':
        return 'Active Educators';
      case 'assessments':
        return 'Assessments Shared';
      case 'completion':
        return 'Completion Rate (%)';
      case 'avgScore':
        return 'Average Score (%)';
    }
  };
  const getTrend = (metric: MetricType) => {
    switch (metric) {
      case 'educators':
        return educatorsTrend;
      case 'assessments':
        return assessmentsTrend;
      case 'completion':
        return completionTrend;
      case 'avgScore':
        return scoreTrend;
    }
  };
  const getTrendText = (educator: EducatorMonthlyData) => {
    const trend = getEducatorTrend(educator);
    if (!trend) return null;
    return trend === 'up' ? '↑' : '↓';
  };
  const metricData = getMetricData(selectedMetric);
  const maxValue = Math.max(...metricData);
  const minValue = Math.min(...metricData);
  // Demo data - Assessment categories
  const assessmentCategories: AssessmentCategory[] = [{
    name: 'Quiz activity',
    count: 45,
    avgScore: 66
  }, {
    name: 'Practice exercises',
    count: 28,
    avgScore: 71
  }, {
    name: 'Image-labeling',
    count: 12,
    avgScore: 63
  }];
  // Permission check for full analytics - MUST be declared early
  const canViewFullAnalytics = currentUserRole === 'Primary Admin' || currentUserRole === 'Secondary Admin';
  // Activity filter state - MUST be declared before filteredEducators
  const [activityFilter, setActivityFilter] = useState<'all' | 'active' | 'attention' | 'inactive'>('all');
  // Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc'); // Default to descending for new field
    }
  };
  // Sort icon component
  const SortIcon = ({
    field,
    className = ''
  }: {
    field: SortField;
    className?: string;
  }) => {
    const isActive = sortField === field;
    if (!isActive) {
      return <ArrowUpDownIcon size={12} className={`text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity ${className}`} data-id="element-3290" />;
    }
    return sortDirection === 'asc' ? <svg className={`w-3 h-3 text-[#00B75F] ${className}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3291">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" data-id="element-3292" />
      </svg> : <svg className={`w-3 h-3 text-[#00B75F] ${className}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3293">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-3294" />
      </svg>;
  };
  // Filter and sort educators for selected month
  const filteredEducators = selectedMonthData.educators.filter(educator => {
    // Role-based filtering: Educators only see themselves
    if (currentUserRole === 'Educator' && educator.id !== currentUserId) {
      return false;
    }
    // Search filter
    return educator.name.toLowerCase().includes(searchQuery.toLowerCase()) || educator.email.toLowerCase().includes(searchQuery.toLowerCase());
  }).sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'assessments':
        comparison = a.assessmentsShared - b.assessmentsShared;
        break;
      case 'participants':
        comparison = a.studentsEngaged - b.studentsEngaged;
        break;
      case 'completion':
        comparison = a.completionRate - b.completionRate;
        break;
      case 'impact':
        comparison = a.engagementScore - b.engagementScore;
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Apply activity filter
  const displayEducators = filteredEducators.filter(educator => {
    if (activityFilter === 'all') return true;
    if (activityFilter === 'active') return educator.assessmentsShared > 0;
    if (activityFilter === 'attention') return canViewFullAnalytics && isCurrentMonth && needsNudge(educator);
    if (activityFilter === 'inactive') return educator.assessmentsShared === 0;
    return true;
  });
  // Count educators by category
  const activityCounts = {
    all: filteredEducators.length,
    active: filteredEducators.filter(e => e.assessmentsShared > 0).length,
    attention: isCurrentMonth ? filteredEducators.filter(e => canViewFullAnalytics && needsNudge(e)).length : 0,
    inactive: filteredEducators.filter(e => e.assessmentsShared === 0).length
  };
  return <Tooltip.Provider delayDuration={200} data-id="element-3295">
      <div className="flex flex-col min-h-screen bg-background" data-id="element-3296">
        <Header data-id="element-3297" />
        <div className="flex flex-1 overflow-hidden" data-id="element-3298">
          <div className="hidden md:block" data-id="element-3299">
            <Sidebar data-id="element-3300" />
          </div>
          <div className="flex-1 flex flex-col min-w-0 w-full overflow-y-auto" data-id="element-3301">
            <main className="flex-1" data-id="element-3302">
              <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-3303">
                {/* Header */}
                <div className="mb-6" data-id="element-3304">
                  <h1 className="text-3xl font-bold text-text-primary mb-2" data-id="element-3305">
                    Organization Overview
                  </h1>
                  <p className="text-text-secondary" data-id="element-3306">
                    Track engagement trends and activity over the last 6 months
                  </p>
                </div>

                {!canViewFullAnalytics ? <div className="bg-surface-elevated rounded-xl border border-custom p-8 text-center mb-8" data-id="element-3307">
                    <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3308">
                      <LockIcon size={32} className="text-text-tertiary" data-id="element-3309" />
                    </div>
                    <h2 className="text-xl font-bold text-text-primary mb-2" data-id="element-3310">
                      Limited Access
                    </h2>
                    <p className="text-text-secondary max-w-md mx-auto" data-id="element-3311">
                      As an Educator, you can only view analytics for your own
                      activity. Full organization insights are available to
                      Admins.
                    </p>
                  </div> : <>
                    {/* Trend Visualization - More compact */}
                    <div className="mb-6" data-id="element-3312">
                      <div className="bg-surface-elevated rounded-xl border border-custom p-5" data-id="element-3313">
                        <div className="mb-4" data-id="element-3314">
                          <div className="flex items-center justify-between mb-3" data-id="element-3315">
                            <div data-id="element-3316">
                              <h2 className="text-lg font-bold text-text-primary" data-id="element-3317">
                                {includeCurrentMonth ? '6-Month Activity Overview' : '5-Month Activity Overview'}
                              </h2>
                              <p className="text-xs text-text-secondary" data-id="element-3318">
                                Click a month to view detailed educator activity
                                below
                              </p>
                            </div>

                            {/* Current Month Toggle - Moved to top right */}
                            <div className="flex items-center gap-2" data-id="element-3319">
                              <label className="flex items-center gap-2 cursor-pointer group" data-id="element-3320">
                                <input type="checkbox" checked={includeCurrentMonth} onChange={e => setIncludeCurrentMonth(e.target.checked)} className="w-4 h-4 rounded border-2 border-custom text-[#00B75F] focus:ring-2 focus:ring-[#00B75F] focus:ring-offset-0 cursor-pointer" data-id="element-3321" />
                                <span className="text-xs text-text-tertiary group-hover:text-text-secondary transition-colors" data-id="element-3322">
                                  Include current month
                                </span>
                              </label>
                              <Tooltip.Root data-id="element-3323">
                                <Tooltip.Trigger asChild data-id="element-3324">
                                  <button className="p-1 rounded hover:bg-surface transition-colors" data-id="element-3325">
                                    <InfoIcon size={14} className="text-text-tertiary" data-id="element-3326" />
                                  </button>
                                </Tooltip.Trigger>
                                <Tooltip.Portal data-id="element-3327">
                                  <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-50" sideOffset={5} data-id="element-3328">
                                    <p className="text-xs font-semibold text-text-primary mb-1" data-id="element-3329">
                                      Why exclude current month?
                                    </p>
                                    <p className="text-xs text-text-secondary leading-relaxed" data-id="element-3330">
                                      The current month is incomplete, which can
                                      skew trends and comparisons. Toggle off to
                                      see data from complete months only for
                                      more accurate insights.
                                    </p>
                                    <Tooltip.Arrow className="fill-surface-elevated" data-id="element-3331" />
                                  </Tooltip.Content>
                                </Tooltip.Portal>
                              </Tooltip.Root>
                            </div>
                          </div>

                          {/* Metric Selector - More compact */}
                          <div className="flex flex-wrap gap-2" data-id="element-3332">
                            <button onClick={() => setSelectedMetric('educators')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${selectedMetric === 'educators' ? 'bg-[#00B75F] text-white' : 'bg-surface text-text-secondary hover:bg-surface-elevated border border-custom'}`} data-id="element-3333">
                              Active Educators
                            </button>
                            <button onClick={() => setSelectedMetric('assessments')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${selectedMetric === 'assessments' ? 'bg-[#00B75F] text-white' : 'bg-surface text-text-secondary hover:bg-surface-elevated border border-custom'}`} data-id="element-3334">
                              Assessments Shared
                            </button>
                            <button onClick={() => setSelectedMetric('completion')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${selectedMetric === 'completion' ? 'bg-[#00B75F] text-white' : 'bg-surface text-text-secondary hover:bg-surface-elevated border border-custom'}`} data-id="element-3335">
                              Completion Rate
                            </button>
                            <button onClick={() => setSelectedMetric('avgScore')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${selectedMetric === 'avgScore' ? 'bg-[#00B75F] text-white' : 'bg-surface text-text-secondary hover:bg-surface-elevated border border-custom'}`} data-id="element-3336">
                              Average Score
                            </button>
                          </div>
                        </div>

                        {/* Bar Chart - More compact */}
                        <div className="space-y-3" data-id="element-3337">
                          {displayMonthlyData.map((data, index) => {
                        const value = metricData[index];
                        const heightPercentage = (value - minValue) / (maxValue - minValue) * 100;
                        const isSelected = index === effectiveSelectedIndex;
                        const isCurrentMonthBar = includeCurrentMonth && index === displayMonthlyData.length - 1;
                        return <div key={index} className="flex items-center gap-3" data-id="element-3338">
                                {/* Month Label */}
                                <div className="w-10 text-xs font-medium text-text-secondary flex items-center gap-1" data-id="element-3339">
                                  {data.month}
                                  {isCurrentMonthBar && <span className="text-xs text-[#FF9D33]" data-id="element-3340">
                                      *
                                    </span>}
                                </div>

                                {/* Bar - Clickable */}
                                <div className="flex-1 relative" data-id="element-3341">
                                  <motion.button onClick={() => setSelectedMonthIndex(includeCurrentMonth ? index : index)} initial={{
                              width: 0
                            }} animate={{
                              width: `${heightPercentage}%`
                            }} transition={{
                              duration: 0.5,
                              delay: index * 0.08,
                              ease: 'easeOut'
                            }} className={`h-10 rounded-lg shadow-sm relative group hover:shadow-md transition-all cursor-pointer ${isSelected ? 'bg-[#2563EB] ring-2 ring-[#2563EB]/30' : isCurrentMonthBar ? 'bg-gradient-to-r from-[#00B75F] to-[#00B75F]/60 hover:from-[#00a355] hover:to-[#00a355]/60' : 'bg-[#00B75F] hover:bg-[#00a355]'}`} data-id="element-3342">
                                    {/* Value Label */}
                                    <div className="absolute inset-0 flex items-center justify-start px-3" data-id="element-3343">
                                      <span className="text-xs font-semibold text-white flex items-center gap-1.5" data-id="element-3344">
                                        {value}
                                        {selectedMetric === 'completion' || selectedMetric === 'avgScore' ? '%' : ''}
                                        {isCurrentMonthBar && <span className="text-xs opacity-90" data-id="element-3345">
                                            (In Progress)
                                          </span>}
                                      </span>
                                    </div>

                                    {/* Tooltip on hover */}
                                    <div className="absolute left-1/2 -translate-x-1/2 -top-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-3346">
                                      <div className="bg-black/90 text-white text-xs font-medium px-3 py-1.5 rounded-lg whitespace-nowrap" data-id="element-3347">
                                        {isCurrentMonthBar ? `${data.month} (Incomplete) - Click to view details` : `Click to view ${data.month} details`}
                                      </div>
                                    </div>
                                  </motion.button>
                                </div>
                              </div>;
                      })}
                        </div>

                        {/* Legend - More compact */}
                        <div className="mt-4 pt-3 border-t border-custom" data-id="element-3348">
                          <div className="flex items-center justify-between text-xs text-text-tertiary" data-id="element-3349">
                            <span data-id="element-3350">
                              {minValue}
                              {selectedMetric === 'completion' || selectedMetric === 'avgScore' ? '%' : ''}
                            </span>
                            <span className="text-text-secondary font-medium" data-id="element-3351">
                              {getMetricLabel(selectedMetric)}
                            </span>
                            <span data-id="element-3352">
                              {maxValue}
                              {selectedMetric === 'completion' || selectedMetric === 'avgScore' ? '%' : ''}
                            </span>
                          </div>

                          {/* Current Month Indicator */}
                          {includeCurrentMonth && <div className="flex items-center gap-2 text-xs mt-2" data-id="element-3353">
                              <span className="text-[#FF9D33] font-bold" data-id="element-3354">
                                *
                              </span>
                              <span className="text-text-tertiary" data-id="element-3355">
                                Current month data is incomplete and may change
                              </span>
                            </div>}
                        </div>
                      </div>
                    </div>
                  </>}

                {/* Section 2: Educator Activity for Selected Month */}
                <div className="mb-8" data-id="element-3356">
                  <div className="mb-4" data-id="element-3357">
                    <div className="flex items-center justify-between mb-3" data-id="element-3358">
                      <div data-id="element-3359">
                        <h2 className="text-lg font-bold text-text-primary mb-1" data-id="element-3360">
                          {canViewFullAnalytics ? `Educator Activity — ${selectedMonthData.month}` : 'Your Contributions'}
                        </h2>
                        <div className="flex items-center gap-2" data-id="element-3361">
                          <CalendarIcon size={14} className="text-[#2563EB]" data-id="element-3362" />
                          <p className="text-xs font-semibold text-[#2563EB]" data-id="element-3363">
                            {selectedMonthData.fullMonth}
                          </p>
                        </div>
                      </div>
                      {canViewFullAnalytics && <div className="text-right" data-id="element-3364">
                          <p className="text-xs text-text-tertiary" data-id="element-3365">
                            Total Assessments
                          </p>
                          <p className="text-xl font-bold text-text-primary" data-id="element-3366">
                            {selectedMonthMetrics.assessmentsShared}
                          </p>
                        </div>}
                    </div>
                    {canViewFullAnalytics && <div className="flex items-center justify-between" data-id="element-3367">
                        <p className="text-xs text-text-secondary" data-id="element-3368">
                          {filteredEducators.length} educator
                          {filteredEducators.length !== 1 ? 's' : ''}{' '}
                          {showArchivedEducators ? 'total' : 'active'} this
                          month
                        </p>

                        {/* Show Archived Toggle */}
                        <button onClick={() => setShowArchivedEducators(!showArchivedEducators)} className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-surface rounded-lg transition-colors border border-custom" data-id="element-3369">
                          {showArchivedEducators ? <>
                              <EyeOffIcon size={14} data-id="element-3370" />
                              Hide Archived
                            </> : <>
                              <EyeIcon size={14} data-id="element-3371" />
                              Show Archived
                            </>}
                        </button>
                      </div>}
                  </div>

                  {/* Search and Sort - Only show if full access */}
                  {canViewFullAnalytics && <div className="mb-4 flex flex-col gap-3" data-id="element-3372">
                      {/* Single row with all filters on desktop, stacked on mobile */}
                      <div className="flex flex-col lg:flex-row gap-3" data-id="element-3373">
                        <div className="relative flex-1" data-id="element-3374">
                          <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary" data-id="element-3375" />
                          <input type="text" placeholder="Search by educator name or email" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-[#00B75F] text-text-primary placeholder-text-tertiary" data-id="element-3376" />
                        </div>

                        {/* Activity Filter */}
                        <div className="relative flex-1 lg:flex-initial lg:w-56" data-id="element-3377">
                          <select value={activityFilter} onChange={e => setActivityFilter(e.target.value as any)} className="w-full px-4 pr-8 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-[#00B75F] text-text-primary appearance-none cursor-pointer" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 8L2 4h8z'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.75rem center'
                      }} data-id="element-3378">
                            <option value="all" data-id="element-3379">
                              All ({activityCounts.all})
                            </option>
                            <option value="active" data-id="element-3380">
                              Active ({activityCounts.active})
                            </option>
                            {isCurrentMonth && activityCounts.attention > 0 && <option value="attention" data-id="element-3381">
                                Attention ({activityCounts.attention})
                              </option>}
                            <option value="inactive" data-id="element-3382">
                              Inactive ({activityCounts.inactive})
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>}

                  {/* Educators Table */}
                  <div className="bg-surface-elevated rounded-xl border-2 border-[#2563EB] border-opacity-20 overflow-hidden" data-id="element-3383">
                    {/* Current Month Indicator */}
                    {isCurrentMonth && <div className="px-6 py-2 bg-[#00B75F] bg-opacity-5 border-b border-[#00B75F] border-opacity-20" data-id="element-3384">
                        <div className="flex items-center gap-2" data-id="element-3385">
                          <div className="w-2 h-2 rounded-full bg-[#00B75F] animate-pulse" data-id="element-3386" />
                          <p className="text-xs font-semibold text-[#00B75F]" data-id="element-3387">
                            Current Month - Activity monitoring active
                          </p>
                        </div>
                      </div>}

                    <div className="overflow-x-auto" data-id="element-3388">
                      <table ref={tableRef} className="w-full" style={{
                      tableLayout: 'fixed'
                    }} data-id="element-3389">
                        <thead className="bg-[#EEF2FF] border-b border-custom" data-id="element-3390">
                          <tr data-id="element-3391">
                            <th onClick={() => handleSort('name')} style={{
                            width: columnWidths.educator
                          }} className="text-left px-4 py-2 text-xs font-bold text-text-secondary uppercase tracking-wide cursor-pointer hover:bg-[#E0E7FF] transition-colors group relative" data-id="element-3392">
                              <div className="flex items-center gap-1.5" data-id="element-3393">
                                Educator
                                <SortIcon field="name" data-id="element-3394" />
                              </div>
                              <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-[#2563EB] hover:bg-opacity-30 transition-colors group/resize" onMouseDown={e => handleResizeStart(e, 'educator')} onClick={e => e.stopPropagation()} data-id="element-3395">
                                <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/resize:opacity-100 transition-opacity" data-id="element-3396">
                                  <GripVerticalIcon size={12} className="text-[#2563EB]" data-id="element-3397" />
                                </div>
                              </div>
                            </th>
                            <th onClick={() => handleSort('assessments')} style={{
                            width: columnWidths.assessments
                          }} className="text-center px-4 py-2 text-xs font-bold text-text-secondary uppercase tracking-wide cursor-pointer hover:bg-[#E0E7FF] transition-colors group relative" data-id="element-3398">
                              <div className="flex items-center justify-center gap-1.5" data-id="element-3399">
                                Assessments
                                <SortIcon field="assessments" data-id="element-3400" />
                              </div>
                              <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-[#2563EB] hover:bg-opacity-30 transition-colors group/resize" onMouseDown={e => handleResizeStart(e, 'assessments')} onClick={e => e.stopPropagation()} data-id="element-3401">
                                <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/resize:opacity-100 transition-opacity" data-id="element-3402">
                                  <GripVerticalIcon size={12} className="text-[#2563EB]" data-id="element-3403" />
                                </div>
                              </div>
                            </th>
                            <th onClick={() => handleSort('participants')} style={{
                            width: columnWidths.participants
                          }} className="text-center px-4 py-2 text-xs font-bold text-text-secondary uppercase tracking-wide cursor-pointer hover:bg-[#E0E7FF] transition-colors group relative" data-id="element-3404">
                              <Tooltip.Root data-id="element-3405">
                                <Tooltip.Trigger asChild data-id="element-3406">
                                  <div className="flex items-center justify-center gap-1 cursor-pointer" data-id="element-3407">
                                    Participants
                                    <InfoIcon size={12} className="text-text-tertiary" data-id="element-3408" />
                                    <SortIcon field="participants" data-id="element-3409" />
                                  </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal data-id="element-3410">
                                  <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-50" sideOffset={5} data-id="element-3411">
                                    <p className="text-xs font-semibold text-text-primary mb-1" data-id="element-3412">
                                      Unique Participants Reached
                                    </p>
                                    <p className="text-xs text-text-secondary leading-relaxed" data-id="element-3413">
                                      Total number of individual participants
                                      who engaged with this educator's
                                      assessments this month (counted once,
                                      regardless of how many assessments they
                                      took)
                                    </p>
                                    <Tooltip.Arrow className="fill-surface-elevated" data-id="element-3414" />
                                  </Tooltip.Content>
                                </Tooltip.Portal>
                              </Tooltip.Root>
                              <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-[#2563EB] hover:bg-opacity-30 transition-colors group/resize" onMouseDown={e => handleResizeStart(e, 'participants')} onClick={e => e.stopPropagation()} data-id="element-3415">
                                <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/resize:opacity-100 transition-opacity" data-id="element-3416">
                                  <GripVerticalIcon size={12} className="text-[#2563EB]" data-id="element-3417" />
                                </div>
                              </div>
                            </th>
                            <th style={{
                            width: columnWidths.context
                          }} className="text-left px-4 py-2 text-xs font-bold text-text-secondary uppercase tracking-wide relative" data-id="element-3418">
                              Context
                              <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-[#2563EB] hover:bg-opacity-30 transition-colors group/resize" onMouseDown={e => handleResizeStart(e, 'context')} onClick={e => e.stopPropagation()} data-id="element-3419">
                                <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/resize:opacity-100 transition-opacity" data-id="element-3420">
                                  <GripVerticalIcon size={12} className="text-[#2563EB]" data-id="element-3421" />
                                </div>
                              </div>
                            </th>
                            <th onClick={() => handleSort('completion')} style={{
                            width: columnWidths.completion
                          }} className="text-left px-4 py-2 text-xs font-bold text-text-secondary uppercase tracking-wide cursor-pointer hover:bg-[#E0E7FF] transition-colors group relative" data-id="element-3422">
                              <Tooltip.Root data-id="element-3423">
                                <Tooltip.Trigger asChild data-id="element-3424">
                                  <div className="flex items-center gap-1 cursor-pointer" data-id="element-3425">
                                    Completion
                                    <InfoIcon size={12} className="text-text-tertiary" data-id="element-3426" />
                                    <SortIcon field="completion" data-id="element-3427" />
                                  </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal data-id="element-3428">
                                  <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-50" sideOffset={5} data-id="element-3429">
                                    <p className="text-xs text-text-secondary" data-id="element-3430">
                                      Average completion rate across all
                                      attempts
                                    </p>
                                    <Tooltip.Arrow className="fill-surface-elevated" data-id="element-3431" />
                                  </Tooltip.Content>
                                </Tooltip.Portal>
                              </Tooltip.Root>
                              <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-[#2563EB] hover:bg-opacity-30 transition-colors group/resize" onMouseDown={e => handleResizeStart(e, 'completion')} onClick={e => e.stopPropagation()} data-id="element-3432">
                                <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/resize:opacity-100 transition-opacity" data-id="element-3433">
                                  <GripVerticalIcon size={12} className="text-[#2563EB]" data-id="element-3434" />
                                </div>
                              </div>
                            </th>
                            <th onClick={() => handleSort('impact')} style={{
                            width: columnWidths.impact
                          }} className="text-center px-4 py-2 text-xs font-bold text-text-secondary uppercase tracking-wide cursor-pointer hover:bg-[#E0E7FF] transition-colors group" data-id="element-3435">
                              <Tooltip.Root data-id="element-3436">
                                <Tooltip.Trigger asChild data-id="element-3437">
                                  <div className="flex items-center justify-center gap-1 cursor-pointer" data-id="element-3438">
                                    Impact
                                    <InfoIcon size={12} className="text-text-tertiary" data-id="element-3439" />
                                    <SortIcon field="impact" data-id="element-3440" />
                                  </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal data-id="element-3441">
                                  <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-sm z-50" sideOffset={5} data-id="element-3442">
                                    <p className="text-xs font-semibold text-text-primary mb-1" data-id="element-3443">
                                      Impact Score
                                    </p>
                                    <p className="text-xs text-text-secondary leading-relaxed mb-2" data-id="element-3444">
                                      You earn 1 point for every participant who
                                      completes an assessment you shared. Only
                                      assessments with 2 or more participants
                                      are counted.
                                    </p>
                                    <div className="pt-2 border-t border-custom" data-id="element-3445">
                                      <p className="text-[10px] text-text-tertiary" data-id="element-3446">
                                        Resets monthly • Self-attempts excluded
                                      </p>
                                    </div>
                                    <Tooltip.Arrow className="fill-surface-elevated" data-id="element-3447" />
                                  </Tooltip.Content>
                                </Tooltip.Portal>
                              </Tooltip.Root>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-custom" data-id="element-3448">
                          {displayEducators.length === 0 ? <tr data-id="element-3449">
                              <td colSpan={6} className="px-4 py-8 text-center text-text-secondary text-sm" data-id="element-3450">
                                {activityFilter === 'all' ? 'No educators found.' : `No ${activityFilter} educators found.`}
                              </td>
                            </tr> : (() => {
                          // Find the top impact score among active educators
                          const topImpactScore = Math.max(...displayEducators.filter(e => e.status !== 'Archived' && e.engagementScore > 0).map(e => e.engagementScore), 0);
                          return displayEducators.map(educator => {
                            const showNudge = canViewFullAnalytics && isCurrentMonth && needsNudge(educator);
                            const isInactive = isZeroActivity(educator);
                            const isArchived = educator.status === 'Archived';
                            const isTopPerformer = !isArchived && !isInactive && educator.engagementScore > 0 && educator.engagementScore === topImpactScore;
                            return <Tooltip.Root key={educator.id} delayDuration={300} data-id="element-3451">
                                    <Tooltip.Trigger asChild data-id="element-3452">
                                      <tr className={`hover:bg-[#2563EB] hover:bg-opacity-5 transition-colors cursor-pointer group ${isArchived ? 'opacity-60' : ''}`} onClick={() => {
                                  if (!isArchived) {
                                    const monthParam = selectedMonthData.month;
                                    navigate(`/organization/educator/${educator.id}/${monthParam}`);
                                  }
                                }} data-id="element-3453">
                                        <td className="px-4 py-2" data-id="element-3454">
                                          <div className="flex items-center gap-2.5" data-id="element-3455">
                                            <div className={`w-8 h-8 rounded-full ${isArchived ? 'bg-gray-200 text-gray-500' : 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]'} flex items-center justify-center font-bold text-xs flex-shrink-0`} data-id="element-3456">
                                              {educator.name.split(' ').map(n => n[0]).join('')}
                                            </div>
                                            <div className="min-w-0 flex-1" data-id="element-3457">
                                              <div className="flex items-center gap-1.5" data-id="element-3458">
                                                <span className={`font-semibold text-sm ${isArchived ? 'text-text-tertiary' : 'text-[#2563EB] group-hover:underline'} truncate`} data-id="element-3459">
                                                  {educator.name}
                                                </span>
                                                {isArchived && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-600" data-id="element-3460">
                                                    <ArchiveIcon size={10} data-id="element-3461" />
                                                    Archived
                                                  </span>}
                                                {showNudge && !isArchived && <button onClick={e => {
                                            e.stopPropagation();
                                            setSelectedEducatorForNudge(educator);
                                            setIsNudgeModalOpen(true);
                                          }} className="p-0.5 rounded hover:bg-[#FF9D33] hover:bg-opacity-10 transition-colors flex-shrink-0" aria-label={`Send reminder to ${educator.name}`} data-id="element-3462">
                                                    <BellIcon size={12} className="text-[#FF9D33]" data-id="element-3463" />
                                                  </button>}
                                              </div>
                                              <p className="text-xs text-text-tertiary truncate" data-id="element-3464">
                                                {educator.email}
                                              </p>
                                            </div>
                                            {!isArchived && <ChevronRightIcon size={18} className="text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 md:block hidden" data-id="element-3465" />}
                                          </div>
                                        </td>

                                        <td className="px-4 py-2 text-center" data-id="element-3466">
                                          <span className={`text-sm font-bold ${isInactive || isArchived ? 'text-text-tertiary' : 'text-[#2563EB]'}`} data-id="element-3467">
                                            {educator.assessmentsShared}
                                          </span>
                                        </td>

                                        <td className="px-4 py-2 text-center" data-id="element-3468">
                                          <span className={`text-sm font-semibold ${isInactive || isArchived ? 'text-text-tertiary' : 'text-text-primary'}`} data-id="element-3469">
                                            {educator.studentsEngaged}
                                          </span>
                                        </td>

                                        <td className="px-4 py-2" data-id="element-3470">
                                          {educator.grades || educator.subjects ? <div className="flex flex-wrap gap-1 max-w-xs" data-id="element-3471">
                                              {educator.grades?.slice(0, 1).map((grade, idx) => <span key={idx} className="inline-flex items-center px-1.5 py-0.5 bg-surface rounded text-xs font-medium text-text-secondary border border-custom" data-id="element-3472">
                                                    {grade}
                                                  </span>)}
                                              {educator.subjects?.slice(0, 1).map((subject, idx) => <span key={idx} className="inline-flex items-center px-1.5 py-0.5 bg-surface rounded text-xs font-medium text-text-secondary border border-custom" data-id="element-3473">
                                                    {subject}
                                                  </span>)}
                                              {(educator.grades?.length || 0) + (educator.subjects?.length || 0) > 2 && <span className="inline-flex items-center px-1.5 py-0.5 bg-surface rounded text-xs font-medium text-text-tertiary border border-custom" data-id="element-3474">
                                                  +
                                                  {(educator.grades?.length || 0) + (educator.subjects?.length || 0) - 2}
                                                </span>}
                                            </div> : <span className="text-xs text-text-tertiary" data-id="element-3475">
                                              —
                                            </span>}
                                        </td>

                                        <td className="px-4 py-2" data-id="element-3476">
                                          {isInactive || isArchived ? <span className="text-xs text-text-tertiary italic" data-id="element-3477">
                                              —
                                            </span> : <span className="text-sm font-medium text-text-primary" data-id="element-3478">
                                              {educator.completionRate}%
                                            </span>}
                                        </td>

                                        <td className="px-4 py-2 text-center" data-id="element-3479">
                                          {isInactive || isArchived ? <span className="text-xs text-text-tertiary italic" data-id="element-3480">
                                              —
                                            </span> : educator.engagementScore === 0 ? <Tooltip.Root data-id="element-3481">
                                              <Tooltip.Trigger asChild data-id="element-3482">
                                                <span className="text-sm font-medium text-text-tertiary cursor-help" data-id="element-3483">
                                                  0
                                                </span>
                                              </Tooltip.Trigger>
                                              <Tooltip.Portal data-id="element-3484">
                                                <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-50" sideOffset={5} data-id="element-3485">
                                                  <p className="text-xs text-text-secondary" data-id="element-3486">
                                                    No eligible assessments yet.
                                                    Assessments need at least 2
                                                    participants to count.
                                                  </p>
                                                  <Tooltip.Arrow className="fill-surface-elevated" data-id="element-3487" />
                                                </Tooltip.Content>
                                              </Tooltip.Portal>
                                            </Tooltip.Root> : <div className="flex items-center justify-center gap-1.5" data-id="element-3488">
                                              {isTopPerformer && <Tooltip.Root data-id="element-3489">
                                                  <Tooltip.Trigger asChild data-id="element-3490">
                                                    <MedalIcon size={14} className="text-[#FF9D33] flex-shrink-0" data-id="element-3491" />
                                                  </Tooltip.Trigger>
                                                  <Tooltip.Portal data-id="element-3492">
                                                    <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-50" sideOffset={5} data-id="element-3493">
                                                      <p className="text-xs font-semibold text-[#FF9D33]" data-id="element-3494">
                                                        🏅 Top Performer!
                                                      </p>
                                                      <p className="text-xs text-text-secondary mt-1" data-id="element-3495">
                                                        Highest impact score
                                                        this month
                                                      </p>
                                                      <Tooltip.Arrow className="fill-surface-elevated" data-id="element-3496" />
                                                    </Tooltip.Content>
                                                  </Tooltip.Portal>
                                                </Tooltip.Root>}
                                              <span className="text-sm font-bold text-[#00B75F]" data-id="element-3497">
                                                {educator.engagementScore}
                                              </span>
                                            </div>}
                                        </td>
                                      </tr>
                                    </Tooltip.Trigger>
                                    {/* Hover tooltip with Last Active - follows cursor */}
                                    {isCurrentMonth && <Tooltip.Portal data-id="element-3498">
                                        <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg z-50 max-w-xs" side="right" sideOffset={10} align="start" data-id="element-3499">
                                          <p className="text-xs font-semibold text-text-primary mb-1" data-id="element-3500">
                                            {educator.name}
                                          </p>
                                          <p className="text-xs text-text-secondary" data-id="element-3501">
                                            Last active:{' '}
                                            {formatDaysSince(getDaysSinceLastActivity(educator))}
                                          </p>
                                          {showNudge && <p className="text-xs text-[#FF9D33] mt-1 font-medium" data-id="element-3502">
                                              ⚠️ Inactive for{' '}
                                              {getDaysSinceLastActivity(educator)}{' '}
                                              days
                                            </p>}
                                          <Tooltip.Arrow className="fill-surface-elevated" data-id="element-3503" />
                                        </Tooltip.Content>
                                      </Tooltip.Portal>}
                                  </Tooltip.Root>;
                          });
                        })()}
                        </tbody>
                      </table>
                    </div>

                    {/* Info Banner for Historical Months */}
                    {!isCurrentMonth && <div className="px-4 py-2 bg-surface border-t border-custom" data-id="element-3504">
                        <div className="flex items-start gap-2" data-id="element-3505">
                          <InfoIcon size={14} className="text-text-tertiary flex-shrink-0 mt-0.5" data-id="element-3506" />
                          <p className="text-xs text-text-secondary" data-id="element-3507">
                            Historical data - Activity monitoring only available
                            for current month
                          </p>
                        </div>
                      </div>}
                  </div>
                </div>

                {/* Section 3: Transparency Message */}
                <div className="p-4 bg-surface rounded-xl border border-custom" data-id="element-3508">
                  <p className="text-sm text-text-secondary text-center" data-id="element-3509">
                    Organization admins see usage and learning insights to
                    support teachers and students. Personal drafts and private
                    work are not visible.
                  </p>
                </div>
              </div>
            </main>
            <Footer data-id="element-3510" />
          </div>
        </div>
      </div>

      {/* Nudge Modal */}
      {isNudgeModalOpen && selectedEducatorForNudge && <NudgeModal educator={{
      id: selectedEducatorForNudge.id,
      name: selectedEducatorForNudge.name,
      email: selectedEducatorForNudge.email
    }} onClose={() => {
      setIsNudgeModalOpen(false);
      setSelectedEducatorForNudge(null);
    }} onSend={async message => {
      console.log('Sending nudge to:', selectedEducatorForNudge.email);
      console.log('Message:', message);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setTimeout(() => {
        setIsNudgeModalOpen(false);
        setSelectedEducatorForNudge(null);
      }, 1500);
    }} data-id="element-3511" />}
    </Tooltip.Provider>;
}